/**
 * Trust check for AUDIO_CHUNK_AVAILABLE postMessage events.
 *
 * The extension delivers chunks via two transports depending on load context:
 *
 *   - Sidepanel iframe: the extension sidepanel (`chrome-extension://<id>`) posts
 *     to the webapp iframe via `iframe.contentWindow.postMessage`. The webapp
 *     receives it with `event.source === window.parent` and
 *     `event.origin === chrome-extension://<id>`.
 *   - Top-level tab: the extension content script (isolated world in the same
 *     frame) calls `window.postMessage(data, '*')`. The webapp receives it in
 *     the same window with `event.source === window`. The origin is the page's
 *     own origin (content scripts can't post from the extension origin), so we
 *     cannot do an extension-origin check here — the `event.source === window`
 *     identity is the gate.
 *
 * A cross-origin iframe embedded inside the page that tries to forge chunks by
 * calling `window.parent.postMessage(...)` reaches the listener with
 * `event.source === that_iframe.contentWindow`, which matches neither case.
 *
 * Security: in the sidepanel-iframe case we require the extension id to be known
 * AND the message origin to equal `chrome-extension://<id>`. The id is established
 * out-of-band by the EXTENSION_METADATA handshake (ChromeExtensionProvider), which
 * itself verifies `event.origin === chrome-extension://<claimed id>` before trusting
 * it. Audio chunks only flow after a recording starts, which can only happen once
 * that handshake has completed — so an unknown id means we are still in the pre-handshake
 * window and must DROP the message rather than trust an unverified parent origin. (A
 * prior version accepted any parent-origin message while the id was null, which would
 * let any extension/page that embeds this iframe inject audio during init.)
 */
export function isTrustedAudioChunkMessage(
    event: Pick<MessageEvent, "source" | "origin">,
    context: { win: Window; extensionId?: string | null },
): boolean {
    const { win, extensionId } = context;
    const isTopLevel = win.parent === win;

    if (isTopLevel) {
        return event.source === win;
    }

    if (event.source !== win.parent) return false;
    // Drop until the extension id is known; never trust an unverified parent origin.
    if (!extensionId) return false;
    return event.origin === `chrome-extension://${extensionId}`;
}
